import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { Icon } from "@iconify/react";
import {
  ButtonPrimary,
  ButtonPrimary1,
  getCompanyName,
  HStack,
  VStack,
} from "../../components/utils";
import { Input, Label } from "../../components/shared/InputField";
import { MultiSelectV2 } from "../../components/shared/MultiSelectV2";
import { ReportType } from "../reports/utils/report";
import { OptionTypes } from "../../components/shared/MultiSelect";
import { ReportDto } from "../../types/Grant";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../utils/DownloadFile";
import { useGetAllExerciseEvents } from "../../queries/transactionV2";
import { Select } from "../../components/shared/Select";
import { ScheduleParams, HissaReportType } from "../../types/common";
import ScheduleSection from "../reports/dialogs/ScheduleSection";
import { useGetAssociatedUsers } from "../../queries/usersRoles";
import ColumnChooserSection from "../reports/dialogs/ColumnChooserSection";
import { getReportColumns } from "../reports/utils/reportColumns";
import {
  deleteReportPreset,
  getReportPresets,
  saveReportPreset,
} from "../../api/reportPresets";
import { AvailableTableFilters, TableName } from "../../types/Table";

const Section10CReportDialog = ({
  onClose = () => {},
  data,
}: Readonly<{
  onClose: () => void;
  data: any;
}>) => {
  const { data: _events } = useGetAllExerciseEvents();
  const events = _events || [];
  const eventOptions: OptionTypes[] = [];
  const ids: string[] = [];
  const [shouldPiiBeMasked, setShouldPiiBeMasked] = useState(false);
  const [scheduleParams, setScheduleParams] = useState<
    ScheduleParams | undefined
  >(undefined);
  const reportColumns = getReportColumns(HissaReportType.SECTION_10_C_REPORT);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    reportColumns.map((c) => c.key)
  );
  const [customHeaders, setCustomHeaders] = useState<Record<string, string>>(
    {}
  );
  const { data: associatedUsers } = useGetAssociatedUsers();

  const reportPresetTableName: TableName = `REPORT_COLUMNS_${HissaReportType.SECTION_10_C_REPORT}`;
  const [savePresetOnGenerate, setSavePresetOnGenerate] =
    useState<boolean>(false);
  const [presetName, setPresetName] = useState<string>("");
  const [baselineColumnState, setBaselineColumnState] = useState(() =>
    JSON.stringify({
      selectedColumns: reportColumns.map((c) => c.key),
      customHeaders: {},
    })
  );
  const [reportPresets, setReportPresets] = useState<AvailableTableFilters[]>(
    []
  );
  const [isPresetLoading, setIsPresetLoading] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string>("");
  const [chooserOrderSyncToken, setChooserOrderSyncToken] = useState(0);

  useEffect(() => {
    getReportPresets(reportPresetTableName).then((presets) => {
      setReportPresets(presets || []);
    });
  }, []);

  const getFilteredHeaders = (
    selected: string[],
    headers: Record<string, string>
  ) =>
    Object.fromEntries(
      Object.entries(headers).filter(
        ([key, value]) => selected.includes(key) && Boolean(value?.trim())
      )
    );

  const getComparableColumnState = () =>
    JSON.stringify({
      selectedColumns,
      customHeaders: getFilteredHeaders(selectedColumns, customHeaders),
    });

  const hasColumnSelectionChanges =
    Boolean(baselineColumnState) &&
    baselineColumnState !== getComparableColumnState();

  const applyPreset = (preset: AvailableTableFilters) => {
    const state = (preset.filterState || {}) as {
      selectedColumns?: string[];
      customHeaders?: Record<string, string>;
      columnLabelSnapshot?: Record<string, string>;
    };
    const presetSelectedColumns = Array.isArray(state.selectedColumns)
      ? state.selectedColumns
      : [];
    const availableColumnKeys = new Set(reportColumns.map((c) => c.key));
    const validSelectedColumns = presetSelectedColumns.filter((key) =>
      availableColumnKeys.has(key)
    );
    if (!validSelectedColumns.length) {
      toast("This preset has no matching columns for the current report", {
        type: "error",
        autoClose: 2500,
      });
      return;
    }
    const selectedSet = new Set(validSelectedColumns);
    const nextCustomHeaders = Object.fromEntries(
      Object.entries(state.customHeaders || {}).filter(
        ([key, value]) =>
          selectedSet.has(key) && Boolean(`${value || ""}`.trim())
      )
    );
    setSelectedColumns(validSelectedColumns);
    setCustomHeaders(nextCustomHeaders);
    setChooserOrderSyncToken((prev) => prev + 1);
    setSelectedPresetId(preset.id);
    setSavePresetOnGenerate(false);
    setPresetName("");
    setBaselineColumnState(
      JSON.stringify({
        selectedColumns: validSelectedColumns,
        customHeaders: nextCustomHeaders,
      })
    );
    toast("Preset applied", { type: "success", autoClose: 1800 });
  };

  const deleteSelectedPreset = async () => {
    if (!selectedPresetId) {
      toast("Please select a preset to delete", {
        type: "error",
        autoClose: 1800,
      });
      return;
    }
    try {
      setIsPresetLoading(true);
      await deleteReportPreset(selectedPresetId);
      const presets = await getReportPresets(reportPresetTableName);
      setReportPresets(presets || []);
      setSelectedPresetId("");
      toast("Preset deleted", { type: "success", autoClose: 1800 });
    } catch (_error) {
      toast("Unable to delete preset", { type: "error", autoClose: 2200 });
    } finally {
      setIsPresetLoading(false);
    }
  };

  const saveCurrentPreset = async (nameOverride?: string) => {
    const cleanName = (nameOverride ?? presetName).trim();
    if (!cleanName) {
      toast("Please enter preset name", { type: "error", autoClose: 1800 });
      return;
    }
    if (!selectedColumns.length) {
      toast("Please select at least one column", {
        type: "error",
        autoClose: 1800,
      });
      return;
    }
    const duplicate = reportPresets.some(
      (preset) => preset.name?.toLowerCase() === cleanName.toLowerCase()
    );
    if (duplicate) {
      toast("A preset with this name already exists", {
        type: "error",
        autoClose: 2200,
      });
      return;
    }
    try {
      setIsPresetLoading(true);
      await saveReportPreset({
        tableName: reportPresetTableName,
        filterName: cleanName,
        filterState: {
          version: 1,
          selectedColumns,
          customHeaders,
          columnLabelSnapshot: Object.fromEntries(
            reportColumns.map((column) => [column.key, column.label])
          ),
        },
      });
      const presets = await getReportPresets(reportPresetTableName);
      setReportPresets(presets || []);
      setPresetName("");
      setSavePresetOnGenerate(false);
      setBaselineColumnState(getComparableColumnState());
      toast("Preset saved", { type: "success", autoClose: 1800 });
    } catch (_error) {
      toast("Error saving preset", { type: "error", autoClose: 1800 });
    } finally {
      setIsPresetLoading(false);
    }
  };

  events.forEach((event) => {
    eventOptions.push({ value: event.eventName });
    ids.push(event.id);
  });

  const initialValues: ReportType = {
    eventIds: [],
    startDate: undefined,
    endDate: undefined,
  };
  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      values.maskPiiData = shouldPiiBeMasked;
      values.selectedColumns = selectedColumns;
      values.customHeaders = Object.fromEntries(
        Object.entries(customHeaders).filter(
          ([key, value]) =>
            selectedColumns.includes(key) && Boolean(value?.trim())
        )
      );
      if (savePresetOnGenerate && hasColumnSelectionChanges) {
        if (!presetName.trim()) {
          toast("Preset name is mandatory when save preset is checked", {
            type: "error",
            autoClose: 1800,
          });
          return;
        }
        try {
          await saveCurrentPreset(presetName.trim());
        } catch (_error) {
          return;
        }
      }
      const payload =
        scheduleParams && scheduleParams.recipients.length > 0
          ? { ...values, scheduleParams }
          : values;
      data
        ?.onSubmitFunction(payload)
        .then((res: any) => {
          toast(
            scheduleParams && scheduleParams.recipients.length > 0
              ? "Report scheduled successfully"
              : "Please wait while your report is being generated",
            { type: "info", autoClose: 2000 }
          );
          downloadReport(res.data);
        })
        .catch((err: any) => {
          toast(
            err?.response?.data?.errorMessage ||
              err?.response?.data?.errors ||
              "Error while downloading the report",
            { type: "error", autoClose: 2000 }
          );
        });
    },
  });
  const fileName = `Section 10C_${getCompanyName()}.xlsx`;
  async function downloadReport(reportDto: ReportDto) {
    onClose();
  }
  const [selectedDateType, setSelectedDateType] = useState("");
  const dateTypes = [
    "As of Date",
    "Current Financial Year",
    "Previous Financial Year",
    "Custom Interval",
  ];
  const handleDateTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value) {
      setSelectedDateType(e.target.value);
      const date = new Date();
      switch (e.target.value) {
        case "As of Date":
          formik.setFieldValue("startDate", "");
          break;
        case "Current Financial Year":
          formik.setFieldValue("startDate", `${date.getFullYear()}-04-01`);
          formik.setFieldValue("endDate", `${date.getFullYear() + 1}-03-31`);
          break;
        case "Previous Financial Year":
          formik.setFieldValue("startDate", `${date.getFullYear() - 1}-04-01`);
          formik.setFieldValue("endDate", `${date.getFullYear()}-03-31`);
          break;
        default:
          break;
      }
    }
  };
  const [showAdvanceFilters, setShowAdvanceFilters] = useState(false);

  return (
    <div className="">
      <div className="p-8 text-lg font-medium border-b">
        <h6 className="flex justify-between">
          {data?.headerName}
          <span
            className="cursor-pointer"
            onClick={() => {
              onClose();
            }}
          >
            X
          </span>
        </h6>
      </div>
      <VStack className="w-full gap-2 px-8 py-4 ">
        <HStack className="gap-8 ">
          <div className="flex-1">
            <Label className="text-sm font-normal">Date Type</Label>
            <Select
              options={dateTypes}
              onChange={(e) => handleDateTypeChange(e)}
              value={selectedDateType}
            />
          </div>
          {selectedDateType !== "As of Date" && (
            <div className="flex-1">
              <Label
                className={`text-sm font-normal ${
                  selectedDateType === "As of Date" ? "cursor-not-allowed" : ""
                }`}
              >
                {data?.startDate}
              </Label>
              <Input
                className={` ${
                  selectedDateType === "As of Date" ? "cursor-not-allowed" : ""
                }`}
                type="date"
                value={formik.values.startDate}
                onBlur={formik.handleBlur}
                disabled={selectedDateType === "As of Date"}
                onChange={(e) => {
                  if (!e.target.value) return;
                  formik.setFieldValue("startDate", e.target.value);
                }}
              />
            </div>
          )}
          <div className="flex-1">
            <Label className={"text-sm font-normal"}>{data?.endDate}</Label>
            <Input
              type="date"
              value={formik.values.endDate}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                if (!e.target.value) return;
                formik.setFieldValue("endDate", e.target.value);
              }}
            />
          </div>
          {selectedDateType === "As of Date" && <div className="flex-1"></div>}
        </HStack>
        <HStack className="pt-2 items-center gap-2 before:border-b-2 before:border-dashed before:m-auto after:flex-1 after:border-dashed after:content-[''] after:border-b-2">
          <ButtonPrimary1
            onClick={() => {
              if (showAdvanceFilters) {
                formik.resetForm();
              }
              setShowAdvanceFilters(!showAdvanceFilters);
            }}
            className="flex flex-row bg-transparent border border-solid cursor-pointer border-orange-501 hover:bg-transparent"
          >
            Advance Filters
            {showAdvanceFilters ? (
              <Icon
                icon="material-symbols:keyboard-arrow-down-rounded "
                className=""
              />
            ) : (
              <Icon
                icon="material-symbols:keyboard-arrow-down-rounded "
                className=""
              />
            )}
          </ButtonPrimary1>
        </HStack>
        {reportPresets.length > 0 && (
          <HStack className="w-full gap-2">
            <div className="flex-1">
              <Label className="text-sm font-medium">
                Column Chooser Presets
              </Label>
              <HStack className="gap-2 items-center">
                <Select
                  className="flex-1 min-w-0"
                  options={reportPresets}
                  value={selectedPresetId}
                  placeholder={
                    isPresetLoading
                      ? "Loading presets..."
                      : "--Select a saved preset--"
                  }
                  valueGetter={(preset) => preset.id}
                  textGetter={(preset) => preset.name || "Untitled Preset"}
                  onChange={(e) => {
                    const preset = reportPresets.find(
                      (entry) => entry.id === e.target.value
                    );
                    if (preset) {
                      applyPreset(preset);
                    } else {
                      setSelectedPresetId("");
                    }
                  }}
                />
                {selectedPresetId && (
                  <ButtonPrimary1
                    onClick={deleteSelectedPreset}
                    className="text-red-500 border-red-300 hover:bg-red-50 bg-transparent"
                  >
                    Delete
                  </ButtonPrimary1>
                )}
              </HStack>
            </div>
            <div className="flex-1"></div>
          </HStack>
        )}
        {showAdvanceFilters && (
          <VStack className="w-full gap-2 py-4">
            <HStack className="gap-8 pt-4">
              <div className="flex-1">
                <Label className={"text-sm font-normal"}>Event Names</Label>
                <MultiSelectV2
                  _className="w-full"
                  multiple={true}
                  placeholder={"--Select events--"}
                  options={eventOptions}
                  handleSelection={(e) => {
                    const eventIds = events
                      .filter((p) => e.includes(p.eventName))
                      .map((p) => p.id);
                    formik.setFieldValue("eventIds", eventIds);
                  }}
                  optionValues={events
                    .filter((p) => formik.values.eventIds?.includes(p.id))
                    .map((p) => p.eventName)}
                />
              </div>
            </HStack>
            <ColumnChooserSection
              columns={reportColumns}
              selectedColumns={selectedColumns}
              customHeaders={customHeaders}
              onSelectedColumnsChange={setSelectedColumns}
              onCustomHeadersChange={setCustomHeaders}
              sortable
              orderSyncToken={chooserOrderSyncToken}
              footerContent={
                hasColumnSelectionChanges ? (
                  <VStack className="gap-2 mt-1">
                    <HStack className="items-center gap-2">
                      <input
                        type="checkbox"
                        checked={savePresetOnGenerate}
                        onChange={(e) => {
                          setSavePresetOnGenerate(e.target.checked);
                          if (!e.target.checked) setPresetName("");
                        }}
                        className="accent-orange-501"
                      />
                      <span className="text-xs text-gray-700">
                        Save this preset
                      </span>
                    </HStack>
                    {savePresetOnGenerate && (
                      <div className="w-full max-w-sm">
                        <Input
                          type="text"
                          value={presetName}
                          onChange={(e) => setPresetName(e.target.value)}
                          placeholder="Preset name"
                          className="w-full"
                        />
                        <div className="text-xxs text-gray-500 mt-1">
                          Preset name is mandatory when save is checked.
                        </div>
                      </div>
                    )}
                  </VStack>
                ) : null
              }
            />
          </VStack>
        )}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <ScheduleSection
            value={scheduleParams}
            onChange={setScheduleParams}
            users={associatedUsers ?? []}
          />
        </div>
        <HStack className="justify-end gap-2 mt-7">
          <input
            type="checkbox"
            checked={shouldPiiBeMasked}
            onChange={() => setShouldPiiBeMasked(!shouldPiiBeMasked)}
            className="text-white accent-orange-501"
            name=""
            id=""
          />
          <span>Hide all Personally Identifiable Information</span>
        </HStack>
        <HStack className="justify-end m-3">
          <p className="italic">Note: The date filters are inclusive </p>
        </HStack>
        <HStack className="justify-end ">
          <ButtonPrimary
            onClick={() => {
              if (selectedColumns.length === 0) {
                toast("Please select at least one column", {
                  type: "error",
                  autoClose: 2000,
                });
                return;
              }
              if (scheduleParams && scheduleParams.recipients.length === 0) {
                toast(
                  "Please select at least one recipient to schedule the report",
                  { type: "error", autoClose: 2000 }
                );
                return;
              }
              formik.handleSubmit();
            }}
          >
            {scheduleParams && scheduleParams.recipients.length > 0
              ? "Generate & Schedule"
              : "Generate"}
          </ButtonPrimary>
        </HStack>
      </VStack>
    </div>
  );
};

export default Section10CReportDialog;
